import { IWACallData } from "@sdk/wa-call-logs/wa-call-logs-model";
import { Avatar } from "components/common/avatar/avatar";
import { ContactAvatar } from "components/common/avatar/client-avatar";
import {
  formatWAUsername,
  getWACloudNativeConnectionData,
  getWACloudNativeDisplayLabel,
  isBsuid,
} from "components/modules/crm/contacts/helpers/get-wa-cloud-native-display-label";
import { loadContactById } from "store/modules/contacts/contacts.helpers";
import { selectContactById } from "store/modules/contacts/contacts.selectors";
import { useQueryWithStore } from "store/store.hooks";

const resolveCallPhone = (callData?: IWACallData) => {
  if (!callData) {
    return undefined;
  }
  for (const candidate of [callData.to, callData.from]) {
    if (candidate?.trim() && !isBsuid(candidate)) {
      return candidate.trim();
    }
  }
  return undefined;
};

/** Contact header for inbound/outbound WhatsApp Cloud Native calls (never shows raw BSUID). */
export const IncomingWACallContactDetails = ({
  contactId,
  connectionId,
  callData,
}: {
  contactId?: string;
  connectionId?: string;
  callData?: IWACallData;
}) => {
  const { state: contact } = useQueryWithStore(
    selectContactById(contactId ?? ""),
    loadContactById(contactId ?? "")(),
    [contactId],
    !contactId,
  );

  const waConnectionData = getWACloudNativeConnectionData(
    contact,
    connectionId,
  );
  const displayName = contact
    ? getWACloudNativeDisplayLabel({ contact })
    : resolveCallPhone(callData) || "Unknown contact";

  const subtitle = waConnectionData?.waUsername?.trim()
    ? formatWAUsername(waConnectionData.waUsername)
    : contact?.data?.primaryMobile ||
      contact?.data?.phones?.[0]?.phone ||
      resolveCallPhone(callData);

  const showSubtitle =
    Boolean(subtitle) && subtitle !== displayName && !isBsuid(subtitle);

  const organization = contact?.data?.organization;

  return (
    <div className="flex items-center gap-3">
      {contactId ? (
        <ContactAvatar
          contactId={contactId}
          connectionId={connectionId}
          size={48}
        />
      ) : (
        <Avatar name={displayName} size={48} />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-gray-900 dark:text-gray-100">
          {displayName}
        </div>
        {showSubtitle && (
          <div className="truncate text-sm text-gray-600 dark:text-gray-400">
            {subtitle}
          </div>
        )}
        {organization && (
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {organization}
          </div>
        )}
      </div>
    </div>
  );
};
